import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type CSSProperties,
    type ReactElement,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Map as MapIcon } from "lucide-react";
import { Badge, EmptyState, Select, Stack, Text } from "@ux/primitives";
import { ConfirmAction, Toolbar } from "@ux/compositions";
import { useCanEdit } from "@shell/auth/useCanEdit";
import { palette, space } from "@ux/tokens";
import { describeApiError } from "../../admin/common/errors";
import { AddAssetDialog } from "./AddAssetDialog";
import { AssetPickerDialog } from "../../admin/assets/AssetPickerDialog";
import { formatAssetDisplay } from "../../admin/assets/display";
import { useAssetEntries } from "../../admin/assets/hooks";
import type { AssetUpsertPayload, AssetView } from "../../admin/assets/types";
import {
    useCreateAssetAssignment,
    useReleaseAssetAssignment,
    useAssetAssignments,
} from "../../admin/asset-assignments/hooks";
import type { AssetAssignmentView } from "../../admin/asset-assignments/types";
import { useSageOffices } from "../../admin/sage-offices/hooks";
import type { SageOfficeView } from "../../admin/sage-offices/types";
import { formatUserDisplay, formatUserShortName } from "../../admin/users/display";
import { useUserDisplayNames } from "../../admin/users/hooks";
import {
    useCreateWorkspaceAssignment,
    useReleaseWorkspaceAssignment,
} from "../../admin/workspace-assignments/hooks";
import { useAddAssetToWorkspace, useCreateWorkspace, useUpdateWorkspace } from "../../admin/workspaces/hooks";
import type { WorkspaceUpsertPayload } from "../../admin/workspaces/types";
import { ReassignAllAssetsDialog } from "../../admin/workspaces/ReassignAllAssetsDialog";
import { DeskProfilePanel } from "./DeskProfilePanel";
import { FloorplanSearch } from "./FloorplanSearch";
import { FloorplanStatsBar } from "./FloorplanStatsBar";
import { FloorplanViewport } from "./FloorplanViewport";
import { isDeskGroupId } from "./engine/svgSetup";
import { useOfficeFloorplan } from "./hooks";
import { officesWithFloorplan, resolveFloorplanSvgUrl } from "./offices";
import {
    computeFloorplanStats,
    floorplanMapElementId,
    floorplanSpaceTitle,
    workspaceKind,
    type FloorplanHoverInfo,
    type FloorplanMarker,
} from "../../admin/workspaces/types";

/**
 * Interactive office floorplan backed by real Workspace / assignment /
 * HR directory APIs. SVG assets are chosen from the Sage office name;
 * desks map via `floorplanElementId` or workspace `name` matching the
 * SVG group id (create from the map, workspace admin, or CSV import).
 */
export function FloorplanPage(): ReactElement {
    const { officeId: officeIdParam } = useParams<{ officeId?: string }>();
    const navigate = useNavigate();
    const canEdit = useCanEdit();

    const officesQuery = useSageOffices({ q: "", includeDeleted: false });
    const allOffices = useMemo(
        () => (officesQuery.data ?? []).filter((o) => !o.isDeleted),
        [officesQuery.data],
    );
    // Floorplan page only offers offices with a dedicated SVG (Lahore / Franklin).
    const offices = useMemo(() => officesWithFloorplan(allOffices), [allOffices]);

    const officeId = useMemo(() => {
        if (officeIdParam && offices.some((o) => o.id === officeIdParam)) {
            return officeIdParam;
        }
        return offices[0]?.id ?? null;
    }, [officeIdParam, offices]);

    const office = offices.find((o) => o.id === officeId) ?? null;
    const svgUrl = office ? resolveFloorplanSvgUrl(office.name) : null;

    useEffect(() => {
        if (!officeId) return;
        if (officeIdParam === officeId) return;
        navigate(`/admin/floorplan/${officeId}`, { replace: true });
    }, [officeId, officeIdParam, navigate]);

    const floorplanQuery = useOfficeFloorplan(officeId);
    const spaces = useMemo(
        () => floorplanQuery.data?.spaces ?? [],
        [floorplanQuery.data?.spaces],
    );

    const occupantIds = useMemo(
        () =>
            spaces
                .map((s) => s.occupantUserId)
                .filter((id): id is string => typeof id === "string" && id.trim() !== ""),
        [spaces],
    );
    const { map: userIndex } = useUserDisplayNames(occupantIds);

    const markers = useMemo((): FloorplanMarker[] => {
        const list: FloorplanMarker[] = [];
        for (const space of spaces) {
            const elementId = floorplanMapElementId(space.workspace);
            if (!elementId) continue;
            const occupantId = space.occupantUserId?.trim() ?? "";
            const occupied = occupantId !== "";
            const assignedName = occupied
                ? formatUserShortName(userIndex.get(occupantId) ?? null)
                : null;
            // On-map label: user → friendly name; group id is applied in svgSetup.
            const label =
                assignedName?.trim() ||
                space.workspace.friendlyName?.trim() ||
                null;
            list.push({
                elementId,
                occupied,
                kind: workspaceKind(space.workspace),
                label,
            });
        }
        return list;
    }, [spaces, userIndex]);

    const spaceByElementId = useMemo(() => {
        const map = new Map<string, (typeof spaces)[number]>();
        for (const space of spaces) {
            const id = floorplanMapElementId(space.workspace);
            if (id) map.set(id, space);
        }
        return map;
    }, [spaces]);

    const stats = useMemo(() => computeFloorplanStats(markers), [markers]);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [query, setQuery] = useState("");
    const [focusNonce, setFocusNonce] = useState(0);
    const [focusRequest, setFocusRequest] = useState<{
        elementId: string;
        token: number;
    } | null>(null);
    const [assignExistingFor, setAssignExistingFor] = useState<string | null>(null);
    const [addAssetFor, setAddAssetFor] = useState<string | null>(null);
    const [reassignAllFor, setReassignAllFor] = useState<string | null>(null);
    const [pendingRelease, setPendingRelease] = useState<AssetAssignmentView | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [assetFormError, setAssetFormError] = useState<string | null>(null);

    useEffect(() => {
        setSelectedId(null);
        setQuery("");
        setFocusRequest(null);
        setActionError(null);
        setAssetFormError(null);
        setAddAssetFor(null);
        setAssignExistingFor(null);
        setReassignAllFor(null);
    }, [officeId]);

    const selectedSpace = selectedId ? (spaceByElementId.get(selectedId) ?? null) : null;
    const selectedWorkspace = selectedSpace?.workspace ?? null;

    const openAssetAssignmentsQuery = useAssetAssignments({
        assetId: "",
        userId: "",
        workspaceId: "",
        targetKind: "workspace",
        openOnly: true,
    });
    const openAssetAssignments = useMemo(
        () => openAssetAssignmentsQuery.data ?? [],
        [openAssetAssignmentsQuery.data],
    );
    const assetIds = useMemo(
        () => openAssetAssignments.map((a) => a.assetId),
        [openAssetAssignments],
    );
    const { map: assetIndex } = useAssetEntries(assetIds);

    const assetsByWorkspaceId = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const assignment of openAssetAssignments) {
            const wsId = assignment.assignedToWorkspaceId?.trim();
            if (!wsId) continue;
            const label = formatAssetDisplay(assetIndex.get(assignment.assetId), assignment.assetId);
            const list = map.get(wsId) ?? [];
            list.push(label);
            map.set(wsId, list);
        }
        return map;
    }, [openAssetAssignments, assetIndex]);

    const hoverInfoByElementId = useMemo(() => {
        const map = new Map<string, FloorplanHoverInfo>();
        for (const space of spaces) {
            const elementId = floorplanMapElementId(space.workspace);
            if (!elementId) continue;
            const ws = space.workspace;
            const kind = workspaceKind(ws);
            const occupied =
                space.occupantUserId !== null &&
                space.occupantUserId !== undefined &&
                space.occupantUserId !== "";
            const occupant =
                occupied && space.occupantUserId
                    ? userIndex.get(space.occupantUserId) ?? null
                    : null;
            const rows: { label: string; value: string }[] = [
                { label: "Type", value: ws.type },
            ];
            // When occupied, title is the person — skip map/group ids.
            if (!occupied) {
                rows.push({ label: "Map id", value: elementId });
                rows.push({ label: "Workspace", value: ws.name });
                if (ws.friendlyName?.trim()) {
                    rows.push({ label: "Friendly name", value: ws.friendlyName.trim() });
                }
            }
            if (occupied && space.occupantUserId) {
                rows.push({
                    label: "Occupant",
                    value: formatUserDisplay(occupant, space.occupantUserId),
                });
                if (occupant?.email) {
                    rows.push({ label: "Email", value: occupant.email });
                }
                if (occupant?.userPrincipalName && occupant.userPrincipalName !== occupant.email) {
                    rows.push({ label: "UPN", value: occupant.userPrincipalName });
                }
            } else if (kind === "desk") {
                rows.push({ label: "Occupant", value: "Vacant" });
            }
            const assets = assetsByWorkspaceId.get(ws.id) ?? [];
            if (assets.length > 0) {
                rows.push({
                    label: assets.length === 1 ? "Asset" : "Assets",
                    value: assets.slice(0, 6).join("; ") + (assets.length > 6 ? "…" : ""),
                });
            } else {
                rows.push({ label: "Assets", value: "None assigned" });
            }
            map.set(elementId, {
                title: floorplanSpaceTitle(
                    occupied ? formatUserShortName(occupant) : null,
                    ws.friendlyName,
                    ws.name?.trim() || elementId,
                ),
                statusLabel: occupied ? "Occupied" : kind === "desk" ? "Vacant" : ws.type,
                rows,
            });
        }
        return map;
    }, [spaces, userIndex, assetsByWorkspaceId]);

    const searchMatchIds = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return null;
        const ids = new Set<string>();
        for (const m of markers) {
            if (m.elementId.toLowerCase().includes(q)) ids.add(m.elementId);
        }
        for (const space of spaces) {
            const el = floorplanMapElementId(space.workspace);
            if (!el) continue;
            const hay = [
                el,
                space.workspace.name,
                space.workspace.friendlyName ?? "",
            ]
                .join(" ")
                .toLowerCase();
            if (hay.includes(q)) ids.add(el);
        }
        return ids;
    }, [markers, spaces, query]);

    const createWorkspaceMutation = useCreateWorkspace();
    const updateWorkspaceMutation = useUpdateWorkspace();
    const createOccupantMutation = useCreateWorkspaceAssignment();
    const releaseOccupantMutation = useReleaseWorkspaceAssignment();
    const addAssetMutation = useAddAssetToWorkspace();
    const createAssetAssignmentMutation = useCreateAssetAssignment();
    const releaseAssetAssignmentMutation = useReleaseAssetAssignment();

    const isBusy =
        createWorkspaceMutation.isPending ||
        updateWorkspaceMutation.isPending ||
        createOccupantMutation.isPending ||
        releaseOccupantMutation.isPending ||
        addAssetMutation.isPending ||
        createAssetAssignmentMutation.isPending ||
        releaseAssetAssignmentMutation.isPending;

    const addAssetWorkspace = useMemo(() => {
        if (addAssetFor === null) return null;
        if (selectedWorkspace?.id === addAssetFor) return selectedWorkspace;
        for (const space of spaces) {
            if (space.workspace.id === addAssetFor) return space.workspace;
        }
        return null;
    }, [addAssetFor, selectedWorkspace, spaces]);

    const focusElement = useCallback((elementId: string) => {
        setSelectedId(elementId);
        setFocusRequest((prev) => ({
            elementId,
            token: (prev?.token ?? 0) + 1,
        }));
    }, []);

    const onSelect = useCallback((elementId: string | null) => {
        setActionError(null);
        setSelectedId(elementId);
    }, []);

    function switchOffice(nextId: string): void {
        navigate(`/admin/floorplan/${nextId}`);
    }

    function handleCreateWorkspace(): void {
        if (!canEdit || !office || !selectedId) return;
        setActionError(null);
        createWorkspaceMutation.mutate(
            {
                id: crypto.randomUUID(),
                sageOfficeId: office.id,
                type: isDeskGroupId(selectedId) ? "Desk" : "Room",
                // Name is the SVG group id — that's how the map matches.
                name: selectedId,
                friendlyName: null,
                floorplanElementId: null,
            },
            {
                onError: (err) => setActionError(describeApiError(err)),
            },
        );
    }

    function handleSaveWorkspace(payload: WorkspaceUpsertPayload): void {
        if (!canEdit) return;
        setActionError(null);
        updateWorkspaceMutation.mutate(payload, {
            onSuccess: (updated) => {
                // If the map id changed, keep the panel on the new shape.
                const nextElementId = floorplanMapElementId(updated);
                if (nextElementId && nextElementId !== selectedId) {
                    setSelectedId(nextElementId);
                }
            },
            onError: (err) => setActionError(describeApiError(err)),
        });
    }

    function handleAssignOccupant(userId: string): void {
        if (!canEdit || !selectedWorkspace) return;
        setActionError(null);
        createOccupantMutation.mutate(
            {
                id: crypto.randomUUID(),
                workspaceId: selectedWorkspace.id,
                userId,
            },
            {
                onError: (err) => setActionError(describeApiError(err)),
            },
        );
    }

    function handleVacate(): void {
        if (!canEdit || !selectedWorkspace?.currentAssignmentId) return;
        setActionError(null);
        releaseOccupantMutation.mutate(
            {
                workspaceId: selectedWorkspace.id,
                assignmentId: selectedWorkspace.currentAssignmentId,
            },
            {
                onError: (err) => setActionError(describeApiError(err)),
            },
        );
    }

    function handleAssignUnassigned(asset: AssetView): void {
        if (assignExistingFor === null) return;
        createAssetAssignmentMutation.mutate(
            {
                id: crypto.randomUUID(),
                assetId: asset.id,
                targetKind: "Workspace",
                workspaceId: assignExistingFor,
            },
            {
                onSuccess: () => setAssignExistingFor(null),
                onError: (err) => setActionError(describeApiError(err)),
            },
        );
    }

    function handleReassignAsset(asset: AssetView, current: AssetAssignmentView): void {
        if (assignExistingFor === null) return;
        const targetWorkspaceId = assignExistingFor;
        reassignAsset(asset.id, current.id, targetWorkspaceId, () => setAssignExistingFor(null));
    }

    function handleAddAssetSave(payload: AssetUpsertPayload): void {
        if (addAssetFor === null) return;
        const workspaceId = addAssetFor;
        setAssetFormError(null);
        addAssetMutation.mutate(
            {
                workspaceId,
                payload: {
                    asset: {
                        id: payload.id,
                        sageOfficeId: payload.sageOfficeId,
                        type: payload.type,
                        make: payload.make,
                        model: payload.model,
                        serialNumber: payload.serialNumber,
                        assetTag: payload.assetTag,
                        purchaseDate: payload.purchaseDate,
                        purchasePrice: payload.purchasePrice,
                    },
                    assignmentId: crypto.randomUUID(),
                },
            },
            {
                onSuccess: () => setAddAssetFor(null),
                onError: (err) => setAssetFormError(describeApiError(err)),
            },
        );
    }

    function handleReassignExistingFromAssetForm(existing: AssetView): void {
        if (addAssetFor === null) return;
        const workspaceId = addAssetFor;
        if (
            existing.currentAssignmentId !== null &&
            existing.currentAssignmentId !== undefined &&
            existing.currentAssignmentId !== ""
        ) {
            reassignAsset(existing.id, existing.currentAssignmentId, workspaceId, () =>
                setAddAssetFor(null),
            );
        } else {
            createAssetAssignmentMutation.mutate(
                {
                    id: crypto.randomUUID(),
                    assetId: existing.id,
                    targetKind: "Workspace",
                    workspaceId,
                },
                {
                    onSuccess: () => setAddAssetFor(null),
                    onError: (err) => setAssetFormError(describeApiError(err)),
                },
            );
        }
    }

    function reassignAsset(
        assetId: string,
        currentAssignmentId: string,
        targetWorkspaceId: string,
        onComplete: () => void,
    ): void {
        releaseAssetAssignmentMutation.mutate(
            { assetId, assignmentId: currentAssignmentId },
            {
                onSuccess: () => {
                    createAssetAssignmentMutation.mutate(
                        {
                            id: crypto.randomUUID(),
                            assetId,
                            targetKind: "Workspace",
                            workspaceId: targetWorkspaceId,
                        },
                        {
                            onSuccess: onComplete,
                            onError: (err) => setActionError(describeApiError(err)),
                        },
                    );
                },
                onError: (err) => setActionError(describeApiError(err)),
            },
        );
    }

    // Global shortcuts: `/` focus search, Escape clear/close.
    useEffect(() => {
        function onKey(e: KeyboardEvent): void {
            const target = e.target as HTMLElement | null;
            const typing =
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable);

            if (e.key === "/" && !typing) {
                e.preventDefault();
                setFocusNonce((n) => n + 1);
                return;
            }
            if (e.key === "Escape") {
                if (selectedId) {
                    setSelectedId(null);
                    return;
                }
                if (query) setQuery("");
            }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [query, selectedId]);

    if (officesQuery.isPending) {
        return (
            <div style={pageStyle}>
                <EmptyState icon={<MapIcon size={28} />} title="Loading offices…" />
            </div>
        );
    }

    if (offices.length === 0) {
        return (
            <div style={pageStyle}>
                <EmptyState
                    icon={<MapIcon size={28} />}
                    title="No mapped offices yet"
                    description="Add a Sage office named Lahore or Franklin to open its floor plan."
                />
            </div>
        );
    }

    if (!office || !svgUrl) {
        return (
            <div style={pageStyle}>
                <Toolbar
                    leading={
                        <Stack direction="row" gap={2} align="center" wrap>
                            <MapIcon size={18} color={palette.accent} />
                            <Text role="heading">Floor plan</Text>
                            <OfficeSelect
                                offices={offices}
                                value={office?.id ?? ""}
                                onChange={switchOffice}
                            />
                        </Stack>
                    }
                />
                <EmptyState
                    icon={<MapIcon size={28} />}
                    title="Select an office"
                    description="Choose Lahore or Franklin to load its floorplan map."
                />
            </div>
        );
    }

    return (
        <div style={pageStyle}>
            <Toolbar
                leading={
                    <Stack direction="row" gap={2} align="center" wrap>
                        <MapIcon size={18} color={palette.accent} />
                        <Text role="heading">Floor plan</Text>
                        <OfficeSelect
                            offices={offices}
                            value={office.id}
                            onChange={switchOffice}
                        />
                        {canEdit ? (
                            <Badge tone="warn">Editor</Badge>
                        ) : (
                            <Badge tone="neutral">View only</Badge>
                        )}
                    </Stack>
                }
                trailing={
                        <FloorplanSearch
                        spaces={spaces}
                        query={query}
                        onQueryChange={setQuery}
                        userIndex={userIndex}
                        onSelectResult={focusElement}
                        onClear={() => {
                            setQuery("");
                            setSelectedId(null);
                        }}
                        focusNonce={focusNonce}
                    />
                }
            />

            <FloorplanStatsBar stats={stats} officeLabel={office.name} />

            {floorplanQuery.isError ? (
                <div style={{ padding: space[4] }}>
                    <Text role="muted" color={palette.danger}>
                        {describeApiError(floorplanQuery.error)}
                    </Text>
                </div>
            ) : null}

            <div style={stageRowStyle}>
                <DeskProfilePanel
                    open={selectedId !== null}
                    elementId={selectedId}
                    workspace={selectedWorkspace}
                    occupantUserId={selectedSpace?.occupantUserId ?? null}
                    canEdit={canEdit}
                    isBusy={isBusy}
                    errorMessage={actionError}
                    onClose={() => setSelectedId(null)}
                    onCreateWorkspace={handleCreateWorkspace}
                    onSaveWorkspace={handleSaveWorkspace}
                    onAssignOccupant={handleAssignOccupant}
                    onVacate={handleVacate}
                    onAddNewAsset={() => {
                        if (selectedWorkspace) {
                            setAssetFormError(null);
                            setAddAssetFor(selectedWorkspace.id);
                        }
                    }}
                    onAssignExistingAsset={() => {
                        if (selectedWorkspace) setAssignExistingFor(selectedWorkspace.id);
                    }}
                    onReleaseAssetAssignment={(assignment) => setPendingRelease(assignment)}
                    onReassignAll={() => {
                        if (selectedWorkspace) setReassignAllFor(selectedWorkspace.id);
                    }}
                    onStartInventory={() => {
                        if (selectedWorkspace) {
                            navigate(
                                `/admin/workspaces/${encodeURIComponent(selectedWorkspace.id)}/inventory`,
                            );
                        }
                    }}
                />
                <FloorplanViewport
                    svgUrl={svgUrl}
                    markers={markers}
                    hoverInfoByElementId={hoverInfoByElementId}
                    selectedId={selectedId}
                    searchMatchIds={searchMatchIds}
                    onSelect={onSelect}
                    focusRequest={focusRequest}
                />
            </div>

            <div style={legendStyle}>
                <Stack direction="row" gap={3} align="center" wrap>
                    <LegendSwatch color="oklch(0.65 0.15 250 / 0.55)" label="Occupied" />
                    <LegendSwatch color="oklch(0.62 0.14 155 / 0.5)" label="Vacant" />
                    <Text role="muted">
                        {markers.length === 0
                            ? "No workspaces mapped yet — click a desk and create one, or create a workspace whose name matches the SVG group id."
                            : `${stats.occupied} occupied · ${stats.vacant} vacant desks on this map`}
                    </Text>
                </Stack>
            </div>

            {selectedWorkspace !== null && assignExistingFor === selectedWorkspace.id ? (
                <AssetPickerDialog
                    open
                    targetWorkspace={selectedWorkspace}
                    onAssignUnassigned={handleAssignUnassigned}
                    onReassignAsset={handleReassignAsset}
                    onCancel={() => setAssignExistingFor(null)}
                    isWorking={
                        createAssetAssignmentMutation.isPending ||
                        releaseAssetAssignmentMutation.isPending
                    }
                />
            ) : null}

            {selectedWorkspace !== null && reassignAllFor === selectedWorkspace.id ? (
                <ReassignAllAssetsDialog
                    sourceWorkspace={selectedWorkspace}
                    onClose={() => setReassignAllFor(null)}
                />
            ) : null}

            {addAssetWorkspace !== null ? (
                <AddAssetDialog
                    open
                    workspaceContext={{
                        id: addAssetWorkspace.id,
                        name: addAssetWorkspace.name,
                        type: addAssetWorkspace.type,
                    }}
                    onSave={handleAddAssetSave}
                    onCancel={() => {
                        setAddAssetFor(null);
                        setAssetFormError(null);
                    }}
                    onReassignExisting={handleReassignExistingFromAssetForm}
                    isSaving={
                        addAssetMutation.isPending ||
                        createAssetAssignmentMutation.isPending ||
                        releaseAssetAssignmentMutation.isPending
                    }
                    errorMessage={assetFormError}
                />
            ) : null}

            <ConfirmAction
                open={pendingRelease !== null}
                title="Release asset from this desk?"
                description="The asset becomes unassigned. You can re-assign it later from Assets or this panel."
                confirmLabel="Release"
                busy={releaseAssetAssignmentMutation.isPending}
                onCancel={() => setPendingRelease(null)}
                onConfirm={() => {
                    if (pendingRelease === null) return;
                    releaseAssetAssignmentMutation.mutate(
                        {
                            assetId: pendingRelease.assetId,
                            assignmentId: pendingRelease.id,
                        },
                        {
                            onSuccess: () => setPendingRelease(null),
                            onError: (err) => {
                                setActionError(describeApiError(err));
                                setPendingRelease(null);
                            },
                        },
                    );
                }}
            />
        </div>
    );
}

function OfficeSelect({
    offices,
    value,
    onChange,
}: {
    readonly offices: readonly SageOfficeView[];
    readonly value: string;
    readonly onChange: (id: string) => void;
}): ReactElement {
    return (
        <div style={{ minWidth: 220 }}>
            <Select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                aria-label="Select office floorplan"
                placeholder="Select office"
                options={offices.map((o) => ({
                    value: o.id,
                    label: o.name,
                }))}
            />
        </div>
    );
}

function LegendSwatch({
    color,
    label,
}: {
    readonly color: string;
    readonly label: string;
}): ReactElement {
    return (
        <Stack direction="row" gap={2} align="center">
            <span
                style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: color,
                    border: `1px solid ${palette.border}`,
                }}
            />
            <Text role="muted">{label}</Text>
        </Stack>
    );
}

const pageStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: palette.canvas,
};

const stageRowStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
};

const legendStyle: CSSProperties = {
    padding: `${space[2]} ${space[4]}`,
    borderTop: `1px solid ${palette.border}`,
    background: palette.surface,
};
